import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../services/api';

interface Location {
  id: string;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  county: string | null;
  postcode: string | null;
  sortOrder: number;
}

const emptyForm = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  county: '',
  postcode: '',
  sortOrder: 0,
};

const ManageLocationsPage: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [deletingLocation, setDeletingLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [addressResults, setAddressResults] = useState<string[]>([]);
  const [showAddressList, setShowAddressList] = useState(false);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await api.get('/locations');
      setLocations(res.data);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load locations', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handlePostcodeLookup = async () => {
    if (!postcodeSearch.trim()) return;

    setLookingUp(true);
    setAddressResults([]);
    setShowAddressList(false);

    try {
      // Call backend API which handles the getAddress.io lookup
      const response = await api.get(`/locations/lookup/${encodeURIComponent(postcodeSearch.trim())}`);
      const data = response.data;

      if (data.source === 'getaddress' && data.addresses?.length > 0) {
        // Full address list from getAddress.io
        const formattedAddresses = data.addresses.map((addr: any) => {
          if (typeof addr === 'string') return addr;
          const parts = [
            addr.line_1,
            addr.line_2,
            addr.line_3,
            addr.line_4,
            addr.locality,
            addr.town_or_city,
            addr.county,
          ].filter(Boolean);
          return parts.join(', ');
        });
        setAddressResults(formattedAddresses);
        setShowAddressList(true);
        setFormData((prev) => ({ ...prev, postcode: data.postcode || postcodeSearch.trim() }));
      } else if (data.source === 'postcodes.io') {
        // Basic area info from postcodes.io fallback
        setFormData((prev) => ({
          ...prev,
          addressLine1: prev.addressLine1 || '',
          city: data.city || '',
          county: data.county || '',
          postcode: data.postcode,
        }));
        setSnackbar({
          open: true,
          message: 'Postcode found! City and county filled in. Enter street address manually.',
          severity: 'success'
        });
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setSnackbar({ open: true, message: 'Postcode not found. Please check and try again.', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: 'Failed to lookup postcode. Please try again.', severity: 'error' });
      }
    } finally {
      setLookingUp(false);
    }
  };

  const handleSelectAddress = (address: string) => {
    // Parse the address string (format: "line1, line2, locality, town, county")
    const parts = address.split(', ').map((p) => p.trim()).filter(Boolean);

    if (parts.length >= 1) {
      const addressLine1 = parts[0] || '';
      const addressLine2 = parts.length > 2 ? parts[1] : '';
      // Try to identify city and county from remaining parts
      const remainingParts = parts.slice(addressLine2 ? 2 : 1);
      const city = remainingParts.length > 0 ? remainingParts[0] : '';
      const county = remainingParts.length > 1 ? remainingParts[remainingParts.length - 1] : '';

      setFormData((prev) => ({
        ...prev,
        addressLine1,
        addressLine2,
        city: city || prev.city,
        county: county || prev.county,
      }));
    }

    setShowAddressList(false);
    setAddressResults([]);
    setSnackbar({ open: true, message: 'Address selected!', severity: 'success' });
  };

  const handleOpenAdd = () => {
    setEditingLocation(null);
    setFormData(emptyForm);
    setPostcodeSearch('');
    setAddressResults([]);
    setShowAddressList(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      addressLine1: location.addressLine1 || '',
      addressLine2: location.addressLine2 || '',
      city: location.city || '',
      county: location.county || '',
      postcode: location.postcode || '',
      sortOrder: location.sortOrder,
    });
    setPostcodeSearch(location.postcode || '');
    setAddressResults([]);
    setShowAddressList(false);
    setDialogOpen(true);
  };

  const handleOpenDelete = (location: Location) => {
    setDeletingLocation(location);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    const payload = {
      name: formData.name.trim(),
      addressLine1: formData.addressLine1.trim() || null,
      addressLine2: formData.addressLine2.trim() || null,
      city: formData.city.trim() || null,
      county: formData.county.trim() || null,
      postcode: formData.postcode.trim() || null,
      sortOrder: formData.sortOrder,
    };
    try {
      if (editingLocation) {
        await api.put(`/locations/${editingLocation.id}`, payload);
        setSnackbar({ open: true, message: 'Location updated', severity: 'success' });
      } else {
        await api.post('/locations', payload);
        setSnackbar({ open: true, message: 'Location added', severity: 'success' });
      }
      setDialogOpen(false);
      fetchLocations();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save location', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingLocation) return;
    setSaving(true);
    try {
      await api.delete(`/locations/${deletingLocation.id}`);
      setSnackbar({ open: true, message: 'Location deleted', severity: 'success' });
      setDeleteDialogOpen(false);
      setDeletingLocation(null);
      fetchLocations();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete location', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Manage Locations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Add and manage event locations for quick selection
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
          Add Location
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon fontSize="small" />
                      Name
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Postcode</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No locations added yet. Click "Add Location" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  locations.map((location) => {
                    const addressParts = [location.addressLine1, location.addressLine2].filter(Boolean).join(', ');
                    return (
                      <TableRow key={location.id} hover>
                        <TableCell>
                          <Typography fontWeight={500}>{location.name}</Typography>
                        </TableCell>
                        <TableCell>
                          {addressParts || (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {location.city ? (
                            <Typography variant="body2">
                              {location.city}{location.county ? `, ${location.county}` : ''}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {location.postcode || (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleOpenEdit(location)} color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleOpenDelete(location)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingLocation ? 'Edit Location' : 'Add Location'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Location Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  fullWidth
                  placeholder="e.g. Team Jak Community Centre"
                />
              </Grid>

              {/* Postcode Lookup Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, mt: 1 }}>
                  Postcode Lookup (UK)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Enter Postcode"
                    value={postcodeSearch}
                    onChange={(e) => setPostcodeSearch(e.target.value.toUpperCase())}
                    placeholder="e.g. BT1 2AA"
                    size="small"
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handlePostcodeLookup();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handlePostcodeLookup}
                    disabled={!postcodeSearch.trim() || lookingUp}
                    sx={{ minWidth: 100 }}
                  >
                    {lookingUp ? <CircularProgress size={20} /> : 'Lookup'}
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Enter a UK postcode to find addresses
                </Typography>

                {/* Address Results Dropdown */}
                <Collapse in={showAddressList && addressResults.length > 0}>
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 1,
                      maxHeight: 200,
                      overflow: 'auto',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <List dense disablePadding>
                      {addressResults.map((address, index) => (
                        <ListItem key={index} disablePadding divider={index < addressResults.length - 1}>
                          <ListItemButton onClick={() => handleSelectAddress(address)}>
                            <ListItemText
                              primary={address}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                  <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                    Select an address from the list above
                  </Typography>
                </Collapse>

                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Address Details
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address Line 1"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  fullWidth
                  placeholder="e.g. 15 High Street"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address Line 2"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  fullWidth
                  placeholder="e.g. Suite 4, Floor 2"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City / Town"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  fullWidth
                  placeholder="e.g. Belfast"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="County"
                  value={formData.county}
                  onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                  fullWidth
                  placeholder="e.g. County Antrim"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Postcode"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                  fullWidth
                  placeholder="e.g. BT1 2AA"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Sort Order"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  fullWidth
                  helperText="Lower numbers appear first"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name.trim() || saving}
          >
            {saving ? 'Saving...' : editingLocation ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Location</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deletingLocation?.name}</strong>? This will not affect existing events that use this location.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={saving}>
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageLocationsPage;
